import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useAuth } from '@/hooks/auth.hook';
import { useAuth0 } from '@auth0/auth0-react';
import { getSafeReturnUrl } from '@/utils/redirectUrl';
import { consumeStudyBuilderDraftToken } from '@/views/home/studyBuilder/studyBuilderDraftToken.js';
import headerlogo from '../../theme/svg/MyMetabolon-color.svg';
import './Login.scss';

/**
 * Login Component - Universal Login
 * 
 * This component redirects to Auth0's hosted Universal Login page.
 * All authentication happens on Auth0's secure hosted page.
 */
const Login = () => {
  const location = useLocation();
  const auth = useAuth();
  const { isLoading: auth0IsLoading, error: auth0Error } = useAuth0();
  const [redirectError, setRedirectError] = useState(null);
  
  // Check for Auth0 callback errors in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (error && errorDescription) {
      const decodedError = decodeURIComponent(errorDescription);
      // Auth0 sends access_denied when the post-login Salesforce check denies login. That check allows
      // login only for Salesforce Contacts and for legacy users (not in Salesforce, created before the
      // legacy cutoff). Leads and other users (e.g. new signups not yet in Salesforce) are denied.
      if (error === 'access_denied') {
        setRedirectError(
          "Almost there, your account is being verified. You'll be notified within 1 business day."
        );
      } else if (decodedError.includes('not authorized to access resource server')) {
        setRedirectError(
          'API Authorization Error: Your application is not authorized to access the API. ' +
          'Please configure API authorization in Auth0 Dashboard: ' +
          'APIs > portal-api > Authorized Applications > Authorize your SPA client.'
        );
      } else {
        setRedirectError(`Authentication Error: ${decodedError}`);
      }
    }
  }, [location.search]);

  useEffect(() => {
    // Wait for Auth0 to initialize before attempting redirect
    if (auth0IsLoading) {
      return;
    }

    // Check for Auth0 errors (e.g. login denied by Action). Prefer URL error_description so
    // we show the actual deny message (e.g. "We'll get back to you.") instead of a generic message.
    if (auth0Error) {
      const urlParams = new URLSearchParams(location.search);
      if (!urlParams.get('error')) {
        setRedirectError(auth0Error.message || 'Failed to initialize authentication. Please refresh the page.');
      }
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('error')) {
      return;
    }

    const params = queryString.parse(location.search);
    const { email, return_url, forgotpassword, src, prompt } = params;
    const safeReturnUrl = getSafeReturnUrl(return_url);
    const forcePromptLogin = prompt === 'login';

    // Set return URL only when it is safe and local to this origin.
    if (safeReturnUrl) {
      localStorage.setItem('returnUrl', safeReturnUrl);
    }

    // Determine which flow to use based on URL params
    const performRedirect = async () => {
      try {
        const draftToken = consumeStudyBuilderDraftToken();
        const authOptions = draftToken ? { draftToken } : {};

        if (forgotpassword) {
          await auth.reset(email || '', null);
        } else if (email) {
          await auth.signup(email, '', src || 'direct', null, authOptions);
        } else {
          await auth.login('', '', null, safeReturnUrl, {
            prompt: forcePromptLogin ? 'login' : undefined,
            ...authOptions,
          });
        }
      } catch (err) {
        setRedirectError('Failed to redirect to login. Please try again.');
      }
    };

    performRedirect();
  }, [location, auth, auth0IsLoading, auth0Error]);

  // Show loading screen while redirecting to Universal Login
  return (
    <div className="OuterContainer" data-testid="login">
      <div className="LogoHeader">
        <img src={headerlogo} alt="Metabolon" />
      </div>
      <div className="LoadingMessage" style={{ 
        textAlign: 'center', 
        padding: '2rem',
        fontSize: '1.2rem',
        color: '#333'
      }}>
        {(redirectError || auth0Error) ? (
          <>
            <p style={{ color: '#d32f2f', marginBottom: '1rem', maxWidth: '600px', margin: '0 auto 1rem' }}>
              {redirectError || auth0Error?.message || 'Authentication error occurred'}
            </p>
            <div style={{ marginTop: '1rem' }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '1rem',
                  backgroundColor: '#0066cc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '0.5rem'
                }}
              >
                Refresh Page
              </button>
              <button
                onClick={() => {
                  void auth.logout('/');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '1rem',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Log Out
              </button>
            </div>
          </>
        ) : auth0IsLoading ? (
          <p>Initializing authentication...</p>
        ) : (
          <>
            <p>Redirecting to secure login...</p>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '1rem' }}>
              If you are not redirected automatically,{' '}
              <a 
                href="#" 
                onClick={(e) => { 
                  e.preventDefault(); 
                  window.location.reload(); 
                }}
                style={{ color: '#0066cc', textDecoration: 'underline' }}
              >
                click here
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// Export as LoginContainer for backward compatibility
export default Login;
export { Login as LoginContainer };
